import React, { useState } from 'react';
import { Target, Tag, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmationDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import type {
  NextEpisodeResponse,
  PatientGoalDetail,
} from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  acceptImportedGoal,
  getPatientGoalsForVisit,
  deleteGoalFromVisit,
  markPatientGoalMet,
  markPatientGoalUnattainable,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import DisplayInterventionCard from './DisplayInterventionCard';
import SourceHistory from './SourceHistory';
import CreateInterventionFromTemplateCard from './CreateInterventionFromTemplateCard';
import AddInterventionsBox from './AddInterventionsBox';
import DisciplineBadge from './DisciplineBadge';
import PushGoalDialog from './PushGoalDialog';
import ImportFindingsList from './ImportFindingsList';
import GoalPerformanceBox from './GoalPerformanceBox';
import GoalPerformanceHistoryTable from './GoalPerformanceHistoryTable';
import { getProgressForVisit } from './getMostRecentProgressWithScore';
import UnattainableReasonDialog from './UnattainableReasonDialog';
import GoalCompletion from './GoalCompletion';
import { goalCompletionDate } from './goalCompletionFields';
import ModifyGoalForm from './ModifyGoalForm';
import { useGoalCardInterventions } from './useGoalCardInterventions';
import { formatDateForDisplay } from '../../utils/dateUtils';

interface DisplayGoalCardProps {
  goal: PatientGoalDetail;
  visitId: string;
  isViewOnly: boolean;
  /**
   * Whether the goal belongs to a discipline other than the visit's.
   * Such cards render view-only and also hide the imported-goal
   * accept action, which `isViewOnly` deliberately leaves available.
   */
  isOtherDiscipline?: boolean;
  /**
   * Whether this visit type can record goal progress. Separate from
   * `isViewOnly`, which tracks visit status: a visit can be perfectly editable
   * and still be the wrong kind of note to record progress on. Required rather
   * than defaulted so a new caller has to state the capability instead of
   * silently opening progress writes the backend will reject.
   */
  canTrackProgress: boolean;
  /**
   * Whether this visit type can attach new interventions. Interventions ride
   * the goal-creation capability (`canAddGoals`): progress-only notes score the
   * existing interventions but cannot add new ones, and the backend rejects the
   * write. Required for the same reason as `canTrackProgress`.
   */
  canAddInterventions: boolean;
  /**
   * Whether this task can mark the goal met or unattainable. True for every
   * progress-eligible visit; an order can never do this (`RECORD_PROGRESS`
   * isn't in its allowed-action set), even though it can still push the goal.
   * Required so a new caller states the capability instead of inheriting
   * `canTrackProgress`'s visit-only meaning.
   */
  canMarkGoalOutcome: boolean;
  /** Whether this task can edit (PUT) the goal. Always true for a visit; always false for an order. */
  canEditGoal: boolean;
  /** Whether this task can delete the goal. Always true for a visit; always false for an order. */
  canDeleteGoal: boolean;
  /**
   * Whether this task can push the goal to the next episode. Separate from
   * `canTrackProgress`/`canMarkGoalOutcome`: an order can push a goal even
   * though it can never record progress or mark it met/unattainable.
   */
  canPushGoal: boolean;
  /** Passed through to each intervention card; see DisplayInterventionCard. */
  canPerformInterventions: boolean;
  /** Passed through to each intervention card; see DisplayInterventionCard. */
  canCompleteInterventions: boolean;
  /** Passed through to each intervention card; see DisplayInterventionCard. */
  canDeleteInterventions: boolean;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /**
   * The episode immediately following the visit's episode, or null when it
   * hasn't been created yet. Fetched once per episode by `GoalsTab` (not per
   * goal card) and passed down so the push-to-next-episode action can be
   * disabled with an explanatory hint instead of offered as a dead button.
   * `undefined` while the parent's fetch is still in flight.
   */
  nextEpisode?: NextEpisodeResponse | null;
  onGoalProgressSaved?: (updatedGoals: PatientGoalDetail[]) => void;
}

const DisplayGoalCard: React.FC<DisplayGoalCardProps> = ({
  goal,
  visitId,
  isViewOnly,
  isOtherDiscipline = false,
  canTrackProgress,
  canAddInterventions,
  canMarkGoalOutcome,
  canEditGoal,
  canDeleteGoal,
  canPushGoal,
  canPerformInterventions,
  canCompleteInterventions,
  canDeleteInterventions,
  episodeStartDate,
  episodeEndDate,
  nextEpisode,
  onGoalProgressSaved,
}) => {
  const isComplete = !!(goal.is_met || goal.is_unattainable);
  // Extended goals have been pushed to the patient's next episode. Like
  // met/unattainable goals, they're frozen against edit/delete/mark-met/
  // mark-unattainable/add-interventions (backend 400s these), but
  // unlike those, progress tracking on the goal where it was documented
  // stays allowed.
  const isExtended = !!goal.is_extended;
  // Push has its own capability (`canPushGoal`) separate from
  // `canTrackProgress`: an order can push a goal even though it can never
  // record progress. It never applies to a goal that is already met,
  // unattainable, or already pushed to the next episode.
  const canShowPushAction =
    !isViewOnly && !isComplete && !goal.is_extended && canPushGoal;
  // The Track Progress dropdown covers three different writes (record
  // progress, mark met, mark unattainable); it should render if any of them
  // is available so a task that can only do one of the three (there is none
  // today, but the props are independent) still gets an entry point.
  const canShowTrackProgressMenu =
    !isViewOnly && !isComplete && (canTrackProgress || canMarkGoalOutcome);
  // Edit/remove: ungated for a visit, always off for an order.
  const canShowEditMenu =
    !isViewOnly && !isComplete && !isExtended && (canEditGoal || canDeleteGoal);
  const { error: showError } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModifyFormOpen, setIsModifyFormOpen] = useState(false);
  const [isAddInterventionsOpen, setIsAddInterventionsOpen] = useState(false);
  const [trackProgressOpen, setTrackProgressOpen] = useState(false);
  const [editDropdownOpen, setEditDropdownOpen] = useState(false);
  const [showGoalPerformanceBox, setShowGoalPerformanceBox] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    'met' | 'unattainable' | null
  >(null);
  const [showDeleteGoalConfirm, setShowDeleteGoalConfirm] = useState(false);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  const {
    freeTextInterventionKeys,
    pendingTemplates,
    savedInterventions,
    handleAddInterventions,
    handleFreeTextInterventionSaved,
    handleCancelFreeText,
    handleUseFreeText,
    handleSaveForTemplate,
    handleCancelForTemplate,
    refreshInterventions,
  } = useGoalCardInterventions({
    visitId,
    patientGoalId: goal.id,
    initialInterventions: goal.interventions,
    onExpand: () => setIsExpanded(true),
  });

  const hasProgressHistory =
    (goal.progress_history?.length ?? 0) > 0 ||
    (goal.source_history?.length ?? 0) > 0 ||
    savedInterventions.some(
      i =>
        (i.progress_history?.length ?? 0) > 0 ||
        (i.source_history?.length ?? 0) > 0
    );

  const typeLabel = goal.length === 'long_term' ? 'Long term' : 'Short term';
  const formattedDate = formatDateForDisplay(goal.target_date ?? '');

  const hasUnperformedInterventions = savedInterventions.some(
    intervention =>
      !(intervention.progress_history?.some(p => p.is_performed) ?? false)
  );

  const mostRecentProgress = getProgressForVisit(
    goal.progress_history,
    visitId
  );
  const progressForCurrentVisit = goal.progress_history?.find(
    p => p.task_id === visitId
  );
  const hasProgressForCurrentVisit = progressForCurrentVisit != null;
  // This visit's record headlines with its own date. Otherwise the goal's
  // latest entry does, and a completion counts as an entry: a goal met with
  // no progress rows otherwise read "last addressed: ---".
  const completionDate = isComplete ? goalCompletionDate(goal) : null;
  const lastAddressedDate =
    mostRecentProgress?.task_id === visitId
      ? mostRecentProgress.task_date
      : [mostRecentProgress?.task_date, completionDate]
          .filter((d): d is string => !!d)
          .sort()
          .pop();
  const lastAddressedValue = lastAddressedDate
    ? formatDateForDisplay(lastAddressedDate)
    : '---';
  const progressValue = mostRecentProgress?.progress_score ?? '---';

  const handleToggleExpand = () => {
    setIsExpanded(prev => !prev);
  };

  const handleOpenAddInterventions = () => {
    setIsAddInterventionsOpen(true);
  };

  const handleCloseAddInterventions = () => {
    setIsAddInterventionsOpen(false);
  };

  const handleTrackPerformanceSelect = () => {
    setShowGoalPerformanceBox(true);
    setTrackProgressOpen(false);
  };

  const handleGoalPerformanceBoxClose = () => {
    setShowGoalPerformanceBox(false);
  };

  const handleGoalPerformanceSaved = () => {
    setShowGoalPerformanceBox(false);
    getPatientGoalsForVisit({
      path: { visit_id: visitId },
      // throwOnError: false would resolve with the AxiosError, silently
      // skipping the refresh — throw so the catch actually fires.
      throwOnError: true,
    })
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(error => {
        console.error(error);
        showError('Failed to load goals');
      });
  };

  const handleMarkAsMet = () => {
    setTrackProgressOpen(false);
    setConfirmAction('met');
  };

  const handleMarkAsUnattainable = () => {
    setTrackProgressOpen(false);
    setConfirmAction('unattainable');
  };

  const handleConfirmAction = () => {
    markPatientGoalMet({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      throwOnError: true,
    })
      .then(() =>
        getPatientGoalsForVisit({
          path: { visit_id: visitId },
          throwOnError: true,
        })
      )
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(error => {
        // Surface the 4xx business message when there is one — e.g. a
        // concurrent push froze this goal between load and this action.
        console.error(error);
        showError(
          'Failed to mark goal as met',
          getBusinessErrorMessage(error) ??
            `Failed to mark goal "${goal.name}" as met.`
        );
      });
    setConfirmAction(null);
  };

  const handleConfirmUnattainable = (reason: string) => {
    markPatientGoalUnattainable({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      body: { unattainable_reason: reason },
      throwOnError: true,
    })
      .then(() =>
        getPatientGoalsForVisit({
          path: { visit_id: visitId },
          throwOnError: true,
        })
      )
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(error => {
        console.error(error);
        showError(
          'Failed to mark goal as unattainable',
          getBusinessErrorMessage(error) ??
            `Failed to mark goal "${goal.name}" as unattainable.`
        );
      });
    setConfirmAction(null);
  };

  const handleCancelConfirmAction = () => {
    setConfirmAction(null);
  };

  const handleModifyGoal = () => {
    setEditDropdownOpen(false);
    setIsModifyFormOpen(true);
  };

  const handleAcceptImport = () => {
    setIsAccepting(true);
    acceptImportedGoal({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      throwOnError: true,
    })
      .then(() =>
        getPatientGoalsForVisit({
          path: { visit_id: visitId },
          throwOnError: true,
        })
      )
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(() => {
        showError('Failed to accept goal');
      })
      .finally(() => setIsAccepting(false));
  };

  const handleModifyCancel = () => {
    setIsModifyFormOpen(false);
  };

  const handleModifySaveSuccess = (updatedGoals: PatientGoalDetail[]) => {
    onGoalProgressSaved?.(updatedGoals);
    setIsModifyFormOpen(false);
  };

  const handleRemoveGoalClick = () => {
    setEditDropdownOpen(false);
    setShowDeleteGoalConfirm(true);
  };

  const handleConfirmDeleteGoal = () => {
    setShowDeleteGoalConfirm(false);
    deleteGoalFromVisit({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      throwOnError: true,
    })
      .then(() =>
        getPatientGoalsForVisit({
          path: { visit_id: visitId },
          throwOnError: true,
        })
      )
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(() => {
        showError(
          'Failed to delete goal',
          `Failed to delete goal "${goal.name}".`
        );
      });
  };

  const handlePushGoalClick = () => {
    setEditDropdownOpen(false);
    setPushDialogOpen(true);
  };

  const handleCancelPush = () => {
    setPushDialogOpen(false);
  };

  const handleGoalPushed = () => {
    setPushDialogOpen(false);
    getPatientGoalsForVisit({
      path: { visit_id: visitId },
      throwOnError: true,
    })
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(error => {
        console.error(error);
        showError('Failed to load goals');
      });
  };

  return (
    <>
      <Card className='self-start'>
        <CardContent className='p-0 space-y-1'>
          {isModifyFormOpen ? (
            <ModifyGoalForm
              goal={goal}
              visitId={visitId}
              episodeStartDate={episodeStartDate}
              episodeEndDate={episodeEndDate}
              onCancel={handleModifyCancel}
              onSaveSuccess={handleModifySaveSuccess}
            />
          ) : (
            <>
              <div className='space-y-3'>
                <div className='space-y-1'>
                  <div className='font-medium text-sm leading-tight'>
                    {goal.name}
                  </div>
                  <p className='text-sm text-muted-foreground leading-relaxed'>
                    {goal.instruction}
                  </p>
                </div>

                <div className='flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/60 pt-2 text-xs text-muted-foreground'>
                  <span className='flex items-center gap-1'>
                    <Target className='h-3 w-3' />
                    {formattedDate}
                    {goal.is_for_end_of_episode && ' (end of episode)'}
                  </span>
                  <span className='flex items-center gap-1'>
                    <Tag className='h-3 w-3' />
                    {typeLabel}
                  </span>
                  {/* Discipline label for QA scanning. Use
                      goal.discipline (own discipline), not disciplines[0]
                      (template applicability — empty for free-text). Hide
                      when null; do not guess from visit or template. */}
                  {goal.discipline != null && (
                    <DisciplineBadge discipline={goal.discipline} />
                  )}
                  {/* The chip shows while the goal still carries its source
                      values untouched (updated_at == imported_at — the same
                      test the overwrite guard uses). Accepting, or any edit,
                      clears it; imported_at stays on progress rows and the
                      print view as permanent provenance. */}
                  {goal.imported_unreviewed && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type='button'
                          aria-label='Imported goal — review options'
                        >
                          <Badge
                            variant='secondary'
                            className='cursor-pointer whitespace-nowrap'
                          >
                            Imported
                            {goal.backdated_creation_date
                              ? ` · initiated ${formatDateForDisplay(goal.backdated_creation_date)}`
                              : ''}
                          </Badge>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align='start'
                        className='w-72 space-y-2 text-sm'
                      >
                        <p>
                          Imported from External EMR. This goal carries its
                          source values until a clinician reviews it.
                        </p>
                        <ImportFindingsList
                          goal={goal}
                          interventions={savedInterventions}
                        />
                        <p className='text-xs text-muted-foreground'>
                          Accept keeps the goal and its interventions as
                          written. Editing the goal also accepts it.
                        </p>
                        {/* Accepting reviews the goal, not the visit, so it
                            stays available on a locked visit and on a met or
                            unattainable goal — the endpoint has no visit-status
                            gate either. Only editing needs an editable visit.
                            An other-discipline card hides it too, since a
                            visit cannot accept another discipline's goal. */}
                        {(!isOtherDiscipline ||
                          (!isViewOnly && !isComplete)) && (
                          <div className='flex justify-end gap-2 pt-1'>
                            {!isViewOnly && !isComplete && (
                              <Button
                                size='sm'
                                variant='outline'
                                onClick={handleModifyGoal}
                              >
                                Edit goal
                              </Button>
                            )}
                            {!isOtherDiscipline && (
                              <Button
                                size='sm'
                                onClick={handleAcceptImport}
                                disabled={isAccepting}
                              >
                                {isAccepting
                                  ? 'Accepting…'
                                  : 'Accept into Roger'}
                              </Button>
                            )}
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  )}
                  <span>
                    last addressed:{' '}
                    <span className='font-semibold text-foreground'>
                      {lastAddressedValue}
                    </span>
                  </span>
                  <span>
                    progress:{' '}
                    <span className='font-semibold text-foreground'>
                      {progressValue}
                    </span>
                  </span>
                  {isExtended && (
                    <span className='whitespace-nowrap rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800'>
                      Extended to next episode
                    </span>
                  )}
                </div>

                {isComplete && <GoalCompletion goal={goal} />}

                <div className='flex flex-wrap items-center gap-2'>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={handleToggleExpand}
                    className='inline-flex items-center gap-1.5'
                  >
                    {/* The panel holds the performance history too, so the
                        label names it. The count is interventions only, so
                        it sits beside that word. */}
                    <span>
                      {isExpanded ? 'Hide interventions' : 'Show interventions'}
                    </span>{' '}
                    <span className='text-muted-foreground'>
                      ({savedInterventions.length})
                    </span>{' '}
                    <span>and history</span>
                    {isExpanded ? (
                      <ChevronUp className='h-3.5 w-3.5' />
                    ) : (
                      <ChevronDown className='h-3.5 w-3.5' />
                    )}
                  </Button>

                  {canShowTrackProgressMenu && (
                    <DropdownMenu
                      open={trackProgressOpen}
                      onOpenChange={setTrackProgressOpen}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          size='sm'
                          variant='outline'
                          className='flex items-center gap-1'
                        >
                          Track Progress
                          <ChevronDown className='h-3 w-3' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        {canTrackProgress && (
                          <DropdownMenuItem
                            onClick={handleTrackPerformanceSelect}
                          >
                            {hasProgressForCurrentVisit
                              ? 'Edit Performance'
                              : 'Track Progress'}
                          </DropdownMenuItem>
                        )}
                        {/* Marking met/unattainable is blocked for an
                            extended goal (a backend guard) -- it's
                            already been pushed forward, so hide these
                            actions rather than let the user hit a 400. */}
                        {!isExtended && canMarkGoalOutcome && (
                          <>
                            <DropdownMenuItem
                              disabled={hasUnperformedInterventions}
                              onClick={handleMarkAsMet}
                            >
                              Mark as met
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={hasUnperformedInterventions}
                              onClick={handleMarkAsUnattainable}
                            >
                              Mark as unattainable
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {/* Editing and removing a goal are not progress actions. The
                      backend leaves goal update and delete ungated on purpose so
                      goals can be corrected wherever they were created, so these
                      stay available on notes that cannot record progress. An
                      order never gets this menu (canEditGoal/canDeleteGoal are
                      both false there). An extended goal is the exception: it's
                      frozen against edit/delete once pushed, so hide
                      this menu. */}
                  {canShowEditMenu && (
                    <DropdownMenu
                      open={editDropdownOpen}
                      onOpenChange={setEditDropdownOpen}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          size='sm'
                          variant='outline'
                          className='flex items-center gap-1'
                        >
                          Edit
                          <ChevronDown className='h-3 w-3' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        {canDeleteGoal && (
                          <DropdownMenuItem
                            disabled={hasProgressHistory}
                            onClick={handleRemoveGoalClick}
                          >
                            Remove goal
                          </DropdownMenuItem>
                        )}
                        {canEditGoal && (
                          <DropdownMenuItem onClick={handleModifyGoal}>
                            Modify goal
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {/* Push is its own capability (`canPushGoal`), independent of
                      the Edit menu above: an order can push a goal to the next
                      episode even though it can never edit/delete/mark it. */}
                  {canShowPushAction && (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={handlePushGoalClick}
                      disabled={!nextEpisode}
                      title={
                        !nextEpisode
                          ? "The next episode hasn't been created yet"
                          : undefined
                      }
                      className='flex items-center gap-1'
                    >
                      Push to next episode
                    </Button>
                  )}
                </div>
              </div>

              {showGoalPerformanceBox && (
                <div className='pt-2 -mx-3 border-t border-border/60'>
                  <GoalPerformanceBox
                    visitId={visitId}
                    patientGoalId={goal.id}
                    existingProgress={progressForCurrentVisit}
                    onCancel={handleGoalPerformanceBoxClose}
                    onSaveSuccess={handleGoalPerformanceSaved}
                  />
                </div>
              )}

              {isExpanded && (
                <div className='mt-1 space-y-2 rounded-md border border-border/60 bg-muted/20 p-3'>
                  <SourceHistory entries={goal.source_history} />
                  {goal.progress_history &&
                    goal.progress_history.length > 0 && (
                      <div>
                        <h4 className='text-xs font-medium text-muted-foreground mb-1'>
                          Performance history
                        </h4>
                        <GoalPerformanceHistoryTable
                          progressHistory={goal.progress_history}
                        />
                      </div>
                    )}
                  {!isViewOnly &&
                    !isComplete &&
                    !isExtended &&
                    canAddInterventions && (
                      <div className='flex'>
                        <Button
                          size='sm'
                          onClick={handleOpenAddInterventions}
                          className='flex items-center gap-1'
                        >
                          <Plus className='h-3 w-3' />
                          Add Interventions
                        </Button>
                      </div>
                    )}
                  {(pendingTemplates.length > 0 ||
                    freeTextInterventionKeys.length > 0) && (
                    <div className='space-y-2 rounded-md border border-primary/20 bg-primary/5 p-2'>
                      <p className='text-xs font-medium text-primary'>
                        Interventions currently being added
                      </p>
                      <div className='grid grid-cols-1 items-start gap-2'>
                        {freeTextInterventionKeys.map(key => (
                          <CreateInterventionFromTemplateCard
                            key={key}
                            template={null}
                            onChange={() => {}}
                            showSaveButton
                            visitId={visitId}
                            patientGoalId={goal.id}
                            onSave={() => handleFreeTextInterventionSaved(key)}
                            onCancel={() => handleCancelFreeText(key)}
                          />
                        ))}
                        {pendingTemplates.map(template => (
                          <CreateInterventionFromTemplateCard
                            key={template.id}
                            template={template}
                            onChange={() => {}}
                            showSaveButton
                            visitId={visitId}
                            patientGoalId={goal.id}
                            onSave={handleSaveForTemplate(template.id)}
                            onCancel={handleCancelForTemplate(template.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {(pendingTemplates.length > 0 ||
                    freeTextInterventionKeys.length > 0) &&
                    savedInterventions.length > 0 && (
                      <div className='relative flex items-center py-1'>
                        <div className='flex-grow border-t border-border' />
                        <span className='mx-3 text-xs text-muted-foreground whitespace-nowrap'>
                          Existing interventions
                        </span>
                        <div className='flex-grow border-t border-border' />
                      </div>
                    )}
                  {savedInterventions.length === 0 &&
                  pendingTemplates.length === 0 &&
                  freeTextInterventionKeys.length === 0 ? (
                    <p className='text-xs text-muted-foreground italic'>
                      No interventions for this goal
                    </p>
                  ) : savedInterventions.length > 0 ? (
                    savedInterventions.map(intervention => (
                      <DisplayInterventionCard
                        key={intervention.id}
                        intervention={intervention}
                        visitId={visitId}
                        isViewOnly={isViewOnly}
                        canTrackProgress={canTrackProgress}
                        canPerformInterventions={canPerformInterventions}
                        canCompleteInterventions={canCompleteInterventions}
                        canDeleteIntervention={canDeleteInterventions}
                        isGoalExtended={isExtended}
                        onPerformanceSaved={refreshInterventions}
                      />
                    ))
                  ) : null}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <AddInterventionsBox
        open={isAddInterventionsOpen}
        onClose={handleCloseAddInterventions}
        visitId={visitId}
        onAddInterventions={handleAddInterventions}
        onUseFreeText={handleUseFreeText}
      />
      <ConfirmationDialog
        open={confirmAction === 'met'}
        title='Mark goal as met?'
        message='This action cannot be undone and will mark all interventions for this patient as complete.'
        onAccept={handleConfirmAction}
        onCancel={handleCancelConfirmAction}
      />
      <UnattainableReasonDialog
        open={confirmAction === 'unattainable'}
        onAccept={handleConfirmUnattainable}
        onCancel={handleCancelConfirmAction}
      />
      <ConfirmationDialog
        open={showDeleteGoalConfirm}
        title='Delete goal?'
        message='You are about to permanently delete this goal. This action cannot be undone.'
        confirmLabel='Delete'
        onAccept={handleConfirmDeleteGoal}
        onCancel={() => setShowDeleteGoalConfirm(false)}
      />
      {nextEpisode && (
        <PushGoalDialog
          goal={goal}
          visitId={visitId}
          nextEpisode={nextEpisode}
          confirmOpen={pushDialogOpen}
          onCancel={handleCancelPush}
          onSuccess={handleGoalPushed}
        />
      )}
    </>
  );
};

export default DisplayGoalCard;
